import { Injectable, inject, signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, tap } from 'rxjs';
import { ApiResponse } from '@app/types';
import { Role } from '@app/types/role.type';

@Injectable({
    providedIn: 'root'
})
export class RoleService {
    private http = inject(HttpClient);
    private readonly API_URL = environment.API_URL;

    private _roles: WritableSignal<Role[]> = signal([]);
    public readonly rolesSignal = this._roles.asReadonly();

    private _isLoading: WritableSignal<boolean> = signal(false);
    public readonly isLoadingSignal = this._isLoading.asReadonly();

    constructor() {
        this.loadRoles().subscribe();
    }

    loadRoles(): Observable<ApiResponse<Role>> {
        this._isLoading.set(true);
        return this.http.post<ApiResponse<Role>>(`${this.API_URL}/roles/read`, { filters: {} }).pipe(
            tap(response => {
                this._roles.set(response.data);
                this._isLoading.set(false);
            })
        );
    }

    createRole(role: Partial<Role>): Observable<ApiResponse<Role>> {
        this._isLoading.set(true);
        return this.http.post<ApiResponse<Role>>(`${this.API_URL}/roles`, [role]).pipe(
            tap(() => {
                this.loadRoles().subscribe();
                this._isLoading.set(false);
            })
        );
    }

    updateRole(id: string, changes: Partial<Role>): Observable<ApiResponse<Role>> {
        this._isLoading.set(true);
        return this.http.patch<ApiResponse<Role>>(`${this.API_URL}/roles`, [{ id, changes }]).pipe(
            tap(() => {
                this.loadRoles().subscribe();
                this._isLoading.set(false);
            })
        );
    }

    deleteRole(id: string): Observable<ApiResponse<any>> {
        this._isLoading.set(true);
        return this.http.request<ApiResponse<any>>('delete', `${this.API_URL}/roles`, {
            body: { ids: [id] }
        }).pipe(
            tap(() => {
                this.loadRoles().subscribe();
                this._isLoading.set(false);
            })
        );
    }

    assignUserToRole(roleId: string, userId: string): Observable<ApiResponse<Role>> {
        return this.http.post<ApiResponse<Role>>(`${this.API_URL}/roles/${roleId}/users/${userId}`, {}).pipe(
            tap(() => this.loadRoles().subscribe())
        );
    }

    removeUserFromRole(roleId: string, userId: string): Observable<ApiResponse<Role>> {
        return this.http.delete<ApiResponse<Role>>(`${this.API_URL}/roles/${roleId}/users/${userId}`).pipe(
            tap(() => this.loadRoles().subscribe())
        );
    }

    getUserRoles(userId: string): Observable<ApiResponse<Role>> {
        return this.http.get<ApiResponse<Role>>(`${this.API_URL}/users/${userId}/roles`);
    }
}
